import type { TableRow } from "@/components/ui/standardized-table";

/**
 * <M-230> A tradução de uma linha da API para as chaves que as tabelas da Taxa de Conversão
 * desenham — o rótulo junto.
 *
 * Seis tabelas desta tela — ESTADO, CIDADE, DISPOSITIVO, GRUPO DE CANAIS, IDADE e GÊNERO —
 * batizam suas colunas em português (`txConversao`, `receitaTotal`, `ticket_medio`...), e
 * nenhuma resposta do servidor tem esses nomes. Enquanto a página só desenhava linhas, a
 * tradução podia morar embutida no JSX; a partir do momento em que o "Total Geral" passa a vir
 * do servidor, ela precisa ser **a mesma** para a linha e para o rodapé — traduzir o rodapé por
 * outro caminho o deixaria vazio na tela sem que nada falhasse, que é exatamente o defeito que
 * M-225 encontrou nas três tabelas da Análise de Ticket Médio.
 *
 * Elas moram aqui, e não no módulo da página, pelo mesmo motivo de `paid-media-total.ts`
 * (M-219) e `rodape-do-conjunto.ts` (M-229): uma regra dentro do módulo da página não tem como
 * ser testada sem desenhar a página inteira, e exportá-la de lá acenderia
 * `react-refresh/only-export-components`.
 *
 * São seis funções de linha sobre três de métricas, e não três: o **rótulo** muda de nome em
 * cada tabela (`estado`, `cidade`, `dispositivo`, `grupo`, `idade`, `genero`) e vem de um campo
 * diferente da resposta, e deixá-lo a cargo de quem chama reabriria o mesmo buraco pelo outro
 * lado — `cidade: totalRow.region` desenharia um rodapé sem nome, com todo teste passando.
 */

/** Receita ÷ pedidos, com zero no lugar da divisão por zero. */
const divisao = (dividendo: number, divisor: number): number =>
  divisor > 0 ? dividendo / divisor : 0;

/**
 * As métricas das tabelas que leem `transactions` — ESTADO, CIDADE e DISPOSITIVO.
 */
export const metricasPorTransacao = (i: TableRow): TableRow => ({
  txConversao: Number(i.conversion_rate || 0),
  change: Number(i.conversion_rate_change_percentage || 0),
  trend: i.conversion_rate_trend_indicator,
  receitaTotal: Number(i.total_revenue || 0),
  changeReceita: Number(i.total_revenue_change_percentage || 0),
  trendReceita: i.total_revenue_trend_indicator,
  sessions: Number(i.sessions || 0),
  sessions_change_percentage: Number(i.sessions_change_percentage || 0),
  total_users: Number(i.total_users || 0),
  total_users_change_percentage: Number(i.total_users_change_percentage || 0),
  orders: Number(i.transactions || 0),
  // `regions-sales` e `devices-sales` não publicam variação de pedidos; o que morava aqui era
  // a variação da **taxa de conversão**, que é outra grandeza. Sem o campo, a coluna fica em
  // zero em vez de mostrar um número de outra coluna.
  orders_change_percentage: Number(i.transactions_change_percentage || 0),
  // Receita sobre pedidos, e zero quando não houve pedido. O `|| 1` que morava aqui fazia a
  // linha com receita e nenhum pedido publicar a **receita inteira** como ticket médio.
  ticket_medio: divisao(
    Number(i.total_revenue || 0),
    Number(i.transactions || 0),
  ),
  ticket_medio_change_percentage: Number(
    i.total_revenue_change_percentage || 0,
  ),
});

/**
 * O mesmo para GRUPO DE CANAIS, que é a única das quatro cujas variações se chamam
 * `*_delta` e cujos pedidos vêm em `orders`, e não em `transactions`.
 */
export const metricasDeCanal = (i: TableRow): TableRow => ({
  txConversao: Number(i.conversion_rate || 0),
  change: Number(i.conversion_rate_delta || 0),
  trend:
    i.conversion_rate_delta != null
      ? Number(i.conversion_rate_delta) > 0
        ? "up"
        : "down"
      : undefined,
  receitaTotal: Number(i.total_revenue || 0),
  changeReceita: Number(i.total_revenue_delta || 0),
  trendReceita:
    i.total_revenue_delta != null
      ? Number(i.total_revenue_delta) > 0
        ? "up"
        : "down"
      : undefined,
  sessions: Number(i.sessions || 0),
  sessions_change_percentage: Number(i.sessions_delta || 0),
  total_users: Number(i.total_users || 0),
  total_users_change_percentage: Number(i.total_users_delta || 0),
  orders: Number(i.orders || 0),
  orders_change_percentage: Number(i.orders_delta || 0),
  ticket_medio: divisao(Number(i.total_revenue || 0), Number(i.orders || 0)),
  ticket_medio_change_percentage: Number(i.total_revenue_delta || 0),
});

/**
 * O mesmo para IDADE e GÊNERO, cuja rota já publica `ticket_medio` pronto — o rodapé
 * dela é o agregado do período inteiro, e não a soma das faixas (M-226).
 */
export const metricasDemograficas = (i: TableRow): TableRow => ({
  txConversao: Number(i.conversion_rate || 0),
  change: Number(i.conversion_rate_change_percentage || 0),
  trend: i.conversion_rate_trend_indicator,
  receitaTotal: Number(i.total_revenue || 0),
  changeReceita: Number(i.total_revenue_change_percentage || 0),
  trendReceita: i.total_revenue_trend_indicator,
  sessions: Number(i.sessions || 0),
  sessions_change_percentage: Number(i.sessions_change_percentage || 0),
  orders: Number(i.orders || 0),
  orders_change_percentage: Number(i.orders_change_percentage || 0),
  ticket_medio: Number(i.ticket_medio || 0),
  ticket_medio_change_percentage: Number(i.ticket_medio_change_percentage || 0),
});

/** A linha de CONVERSÃO POR ESTADO — `regions-sales/by-state`. */
export const linhaDeEstado = (i: TableRow): TableRow => ({
  estado: i.region,
  ...metricasPorTransacao(i),
});

/**
 * A linha de CONVERSÃO POR CIDADE — `regions-sales/by-city`.
 *
 * O `|| i.region` não é sobra: a mesma rota serve as duas dimensões, e `regions-sales` sem
 * `dimension=city` publica o rótulo em `region` com `city` nulo.
 */
export const linhaDeCidade = (i: TableRow): TableRow => ({
  cidade: i.city || i.region || "N/D",
  ...metricasPorTransacao(i),
});

/** A linha de CONVERSÃO POR DISPOSITIVOS — `devices-sales`. */
export const linhaDeDispositivo = (i: TableRow): TableRow => ({
  dispositivo: i.device,
  ...metricasPorTransacao(i),
});

/** A linha de CONVERSÃO POR GRUPO CANAIS — `acquisition/channel-grouping`. */
export const linhaDeGrupoDeCanais = (i: TableRow): TableRow => ({
  grupo: i.channel_group,
  ...metricasDeCanal(i),
});

/** A linha de CONVERSÃO POR IDADE — bloco `ages` de `conversion-rate/demographics-v2`. */
export const linhaDeIdade = (i: TableRow): TableRow => ({
  idade: i.age,
  ...metricasDemograficas(i),
});

/** A linha de CONVERSÃO POR GÊNERO — bloco `genders` da mesma resposta. */
export const linhaDeGenero = (i: TableRow): TableRow => ({
  genero: i.gender,
  ...metricasDemograficas(i),
});
